// Error logging and monitoring service
// In production, integrate with services like Sentry, LogRocket, or Rollbar

const ERROR_LOGGING_ENDPOINT = process.env.REACT_APP_ERROR_LOG_ENDPOINT;
const ENVIRONMENT = process.env.NODE_ENV;
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

/**
 * Log an error to monitoring service
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @param {object} additionalData - Additional contextual data
 */
export const logError = (error, context = 'unknown', additionalData = {}) => {
  const errorData = {
    message: error?.message || 'Unknown error',
    stack: error?.stack || '',
    context,
    timestamp: new Date().toISOString(),
    environment: ENVIRONMENT,
    appVersion: APP_VERSION,
    url: window.location.href,
    userAgent: navigator.userAgent,
    ...additionalData
  };

  // Log to console in development
  if (ENVIRONMENT === 'development') {
    console.error(`[${context}]`, error, additionalData);
  }

  // Send to logging service in production
  if (ENVIRONMENT === 'production' && ERROR_LOGGING_ENDPOINT) {
    sendToLoggingService(errorData);
  }
};

/**
 * Log a warning message
 * @param {string} message - Warning message
 * @param {string} context - Context where the warning occurred
 * @param {object} additionalData - Additional contextual data
 */
export const logWarning = (message, context = 'unknown', additionalData = {}) => {
  const warningData = {
    message,
    context,
    timestamp: new Date().toISOString(),
    environment: ENVIRONMENT,
    appVersion: APP_VERSION,
    url: window.location.href,
    ...additionalData
  };

  if (ENVIRONMENT === 'development') {
    console.warn(`[${context}] ${message}`, additionalData);
  }

  if (ENVIRONMENT === 'production' && ERROR_LOGGING_ENDPOINT) {
    sendToLoggingService(warningData, 'warning');
  }
};

/**
 * Log an info message
 * @param {string} message - Info message
 * @param {string} context - Context where the info occurred
 * @param {object} additionalData - Additional contextual data
 */
export const logInfo = (message, context = 'unknown', additionalData = {}) => {
  const infoData = {
    message,
    context,
    timestamp: new Date().toISOString(),
    environment: ENVIRONMENT,
    appVersion: APP_VERSION,
    ...additionalData
  };

  if (ENVIRONMENT === 'development') {
    console.log(`[${context}] ${message}`, additionalData);
  }

  if (ENVIRONMENT === 'production' && ERROR_LOGGING_ENDPOINT) {
    sendToLoggingService(infoData, 'info');
  }
};

/**
 * Track API errors
 * @param {string} endpoint - API endpoint
 * @param {number} statusCode - HTTP status code
 * @param {string} errorMessage - Error message from API
 */
export const logApiError = (endpoint, statusCode, errorMessage) => {
  logError(
    new Error(errorMessage),
    'API_ERROR',
    {
      endpoint,
      statusCode,
      severity: statusCode >= 500 ? 'high' : 'medium'
    }
  );
};

/**
 * Send error data to external logging service
 * @param {object} errorData - Error data to send
 * @param {string} level - Log level (error, warning, info)
 */
const sendToLoggingService = async (errorData, level = 'error') => {
  if (!ERROR_LOGGING_ENDPOINT) {
    return;
  }

  try {
    await fetch(ERROR_LOGGING_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        level,
        data: errorData
      })
    });
  } catch (fetchError) {
    // Silently fail - don't throw errors from error logging
    console.error('Failed to send error log:', fetchError);
  }
};

/**
 * Set up global error handler
 */
export const setupGlobalErrorHandlers = () => {
  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), 'UNCAUGHT_ERROR', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason || new Error('Unhandled promise rejection'), 'UNHANDLED_REJECTION');
  });
};
